/**
 * Wishlist Functionality
 * Handles adding/removing products from wishlist using localStorage
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.wishlist = this.loadWishlist();
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateAllButtons();
    this.updateWishlistCount();
    this.initSoldOutWishlistButtons();
  }

  bindEvents() {
    // Bind click events to all wishlist buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-wishlist-button]')) {
        e.preventDefault();
        const button = e.target.closest('[data-wishlist-button]');
        this.handleWishlistToggle(button);
      }
    });

    // Update buttons when page loads
    document.addEventListener('DOMContentLoaded', () => {
      this.updateAllButtons();
    });
  }

  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading wishlist:', error);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.updateWishlistCount();
      document.dispatchEvent(new CustomEvent('wishlistUpdated', {
        detail: { items: this.getWishlistItems() }
      }));
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  addToWishlist(productData) {
    const existingIndex = this.wishlist.findIndex(item => item.id === productData.id);
    
    if (existingIndex === -1) {
      this.wishlist.push({
        id: productData.id,
        handle: productData.handle,
        title: productData.title,
        url: productData.url,
        image: productData.image,
        price: productData.price,
        vendor: productData.vendor,
        available: productData.available === 'true',
        addedAt: new Date().toISOString()
      });
      
      this.saveWishlist();
      this.showNotification('added', productData.title);
      return true;
    }
    
    return false;
  }

  removeFromWishlist(productId) {
    const existingIndex = this.wishlist.findIndex(item => item.id === productId);
    
    if (existingIndex !== -1) {
      const removedItem = this.wishlist[existingIndex];
      this.wishlist.splice(existingIndex, 1);
      this.saveWishlist();
      this.showNotification('removed', removedItem.title);
      return true;
    }
    
    return false;
  }

  isInWishlist(productId) {
    return this.wishlist.some(item => item.id === productId);
  }

  getWishlistCount() {
    return this.wishlist.length;
  }

  getWishlistItems() {
    return [...this.wishlist];
  }

  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
    this.updateAllButtons();
  }

  handleWishlistToggle(button) {
    const productId = button.dataset.productId;
    const productData = {
      id: productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      vendor: button.dataset.productVendor,
      available: button.dataset.productAvailable
    };

    // Add loading state
    button.classList.add('loading');
    button.disabled = true;

    // Simulate API call delay for better UX
    setTimeout(() => {
      const isInWishlist = this.isInWishlist(productId);
      
      if (isInWishlist) {
        this.removeFromWishlist(productId);
        button.dataset.inWishlist = 'false';
        button.classList.remove('added');
      } else {
        this.addToWishlist(productData);
        button.dataset.inWishlist = 'true';
        button.classList.add('added');
      }

      // Remove loading state
      button.classList.remove('loading');
      button.disabled = false;

      // Update button appearance
      this.updateButtonState(button);
    }, 300);
  }

  updateButtonState(button) {
    const isInWishlist = button.dataset.inWishlist === 'true';
    
    if (isInWishlist) {
      button.setAttribute('aria-label', 'İstek listesinden çıkar');
      button.setAttribute('title', 'İstek listesinden çıkar');
    } else {
      button.setAttribute('aria-label', 'İstek listesine ekle');
      button.setAttribute('title', 'İstek listesine ekle');
    }
  }

  initSoldOutWishlistButtons() {
    const wrappers = document.querySelectorAll('[data-sold-out-wishlist]');

    wrappers.forEach(wrapper => {
      const container = wrapper.parentElement;
      const addToCartButton = container && container.querySelector('[data-add-to-cart]');
      if (!addToCartButton) return;

      const syncVisibility = () => {
        wrapper.hidden = !addToCartButton.disabled;
      };

      syncVisibility();
      new MutationObserver(syncVisibility).observe(addToCartButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled']
      });
    });
  }

  updateAllButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-button]');
    
    buttons.forEach(button => {
      const productId = button.dataset.productId;
      const isInWishlist = this.isInWishlist(productId);
      
      button.dataset.inWishlist = isInWishlist.toString();
      this.updateButtonState(button);
    });
  }

  updateWishlistCount() {
    const count = this.getWishlistCount();
    const countElements = document.querySelectorAll('[data-wishlist-count]');
    
    countElements.forEach(element => {
      element.textContent = count;
      element.style.display = count > 0 ? 'inline' : 'none';
    });

    // Update header wishlist link if exists
    const headerLink = document.querySelector('[data-wishlist-header-link]');
    if (headerLink) {
      const countBadge = headerLink.querySelector('[data-wishlist-count]');
      if (countBadge) {
        countBadge.textContent = count;
        countBadge.style.display = count > 0 ? 'inline' : 'none';
      }
    }
  }

  showNotification(action, productTitle) {
    // Remove existing notifications
    const existingNotifications = document.querySelectorAll('.wishlist-notification');
    existingNotifications.forEach(notification => notification.remove());

    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    
    const icon = action === 'added' ? 
      '<svg class="wishlist-notification__icon" viewBox="0 0 16 16" fill="currentColor"><path d="M8 14L6.5 12.5C3.5 9.5 1 7.5 1 5C1 2.5 2.5 1 5 1C6 1 7 1.5 8 2.5C9 1.5 10 1 11 1C13.5 1 15 2.5 15 5C15 7.5 12.5 9.5 9.5 12.5L8 14Z" fill="currentColor"/></svg>' :
      '<svg class="wishlist-notification__icon" viewBox="0 0 16 16" fill="currentColor"><path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    
    const message = action === 'added' ?
      `“${productTitle}” istek listesine eklendi` :
      `“${productTitle}” istek listesinden çıkarıldı`;
    
    notification.innerHTML = `
      <div class="wishlist-notification__content">
        ${icon}
        <div>
          <div class="wishlist-notification__text">${message}</div>
          ${action === 'added' ? '<a class="wishlist-notification__link" href="/pages/wishlist">İstek listesini görüntüle</a>' : ''}
        </div>
      </div>
      <button class="wishlist-notification__close" type="button" aria-label="Bildirimi kapat">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
          <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    `;

    // Add to page
    document.body.appendChild(notification);

    // Show notification
    setTimeout(() => {
      notification.classList.add('show');
    }, 100);

    // Auto-hide after 4 seconds
    setTimeout(() => {
      this.hideNotification(notification);
    }, 4000);

    // Close button functionality
    const closeButton = notification.querySelector('.wishlist-notification__close');
    closeButton.addEventListener('click', () => {
      this.hideNotification(notification);
    });
  }

  hideNotification(notification) {
    notification.classList.remove('show');
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
  }

  // Public API methods
  addProduct(productData) {
    return this.addToWishlist(productData);
  }

  removeProduct(productId) {
    return this.removeFromWishlist(productId);
  }

  hasProduct(productId) {
    return this.isInWishlist(productId);
  }

  getCount() {
    return this.getWishlistCount();
  }

  getAllItems() {
    return this.getWishlistItems();
  }

  clear() {
    this.clearWishlist();
  }
}

// Initialize wishlist when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.wishlist = new WishlistManager();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = WishlistManager;
}
